import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core'

import { Nullable } from 'primeng/ts-helpers'

import { DeviceService } from '@ti/core/common/services'
import { CheckIconComponent } from '@ti/core/icons/check'

import { DropdownSelectMenuItem, OptionsItem } from '../../../contracts/dropdown'
import { BottomDialogComponent } from '../../bottom-dialog/bottom-dialog.component'
import { MenuComponent } from '../../menu/menu.component'
import { TieredMenuComponent } from '../../tiered-menu/tiered-menu.component'

interface SheetOption {
  option: DropdownSelectMenuItem
  selected: boolean
}

interface SheetGroup {
  label?: string
  options: SheetOption[]
}

@Component({
  selector: 'app-dropdown-select',
  templateUrl: 'dropdown-select.component.html',
  styleUrl: 'dropdown-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BottomDialogComponent, MenuComponent, TieredMenuComponent],
})
export class DropdownSelectComponent<V = unknown> {
  public readonly value = input<Nullable<V[]>>()
  public readonly options = input<DropdownSelectMenuItem[]>([])
  public readonly actions = input<Nullable<OptionsItem[]>>([])
  public readonly startText = input<string | undefined>('')
  public readonly mobilePanelTitle = input<string | undefined>()
  public readonly mobileSheet = input<boolean>(true)
  public readonly appendTo = input<string | undefined>(undefined)
  public readonly showIcon = input<boolean>(true)

  @ViewChild('menu')
  protected readonly menu?: TieredMenuComponent

  protected readonly device = inject(DeviceService)

  protected readonly visible = signal(false)

  protected readonly allOptions = computed(() => {
    const actions = this.actions()
    const optionsPrepared = this.prepareOptions(this.options(), this.selectedOptions())
    return actions?.length ? [...optionsPrepared, ...this.prepareActions(actions)] : optionsPrepared
  })

  protected readonly selectedOptions = computed(() => this.value() ?? [])

  protected readonly hasNestedOptions = computed(() =>
    this.allOptions().some((option) => option.items),
  )

  protected readonly useSheet = computed(() => this.mobileSheet() && this.device.isMobile())

  protected readonly sheetTitle = computed(() => this.mobilePanelTitle() || this.startText())

  /** Nested options become labelled groups and actions follow as their own group. */
  protected readonly sheetGroups = computed<SheetGroup[]>(() => {
    const selected = this.selectedOptions()
    const toSheetOptions = (options: DropdownSelectMenuItem[]): SheetOption[] =>
      options
        .filter((option) => option.visible !== false)
        .map((option) => ({
          option,
          selected: option.value !== undefined && selected.includes(option.value),
        }))

    const groups: SheetGroup[] = []
    let plainGroup: SheetGroup | undefined
    for (const option of toSheetOptions(this.options())) {
      if (option.option.items) {
        plainGroup = undefined
        groups.push({ label: option.option.label, options: toSheetOptions(option.option.items) })
      } else {
        if (!plainGroup) {
          plainGroup = { options: [] }
          groups.push(plainGroup)
        }
        plainGroup.options.push(option)
      }
    }
    groups.push({ options: toSheetOptions(this.actions() ?? []) })

    return groups.filter((group) => group.options.length)
  })

  public toggle($event: MouseEvent) {
    if (this.useSheet()) {
      if (this.sheetGroups().length) {
        this.visible.set(true)
      }
      return
    }

    this.menu?.toggle($event)
  }

  protected selectSheetOption(event: Event, option: DropdownSelectMenuItem) {
    if (option.disabled) {
      return
    }

    this.visible.set(false)
    option.command?.({ originalEvent: event, item: option })
  }

  protected prepareActions(actions: OptionsItem[]) {
    if (actions?.length && this.startText()) {
      actions[0].styleClass = 'has-top-border'
    }
    return actions
  }

  protected prepareOptions(
    options: DropdownSelectMenuItem[],
    selectedOptions: V[] | null,
  ): DropdownSelectMenuItem[] {
    return options.map((option) => {
      const isSelected = selectedOptions?.includes(option.value)
      if (this.showIcon()) {
        return {
          ...option,
          icon: isSelected ? '' : 'pi',
          customIcon: isSelected ? CheckIconComponent : undefined,
          styleClass: `${option.styleClass} ${isSelected ? 'selected' : ''}`,
          items: option.items ? this.prepareOptions(option.items, selectedOptions) : undefined,
        }
      } else {
        return {
          ...option,
          styleClass: `${option.styleClass} ${isSelected ? 'selected' : ''}`,
          items: option.items ? this.prepareOptions(option.items, selectedOptions) : undefined,
        }
      }
    })
  }
}
